import { Spinner } from '@chakra-ui/react'
import { Component } from 'react'
import {
  ConfigurationDetail,
  ItemIdentifier,
  ItemIdentifiers,
  IdentifierEvents,
  ProductionOrderRow,
  WorkOrderRow,
  WorkOrderRows,
  EncodingProductionOrderRows,
  Items,
  TmrItem,
  ProductInfoField
} from 'stylewhere/api'
import { Box, InfoBox, ItemEventBox, Modal, ModalTitle, ProductInfoBox, Spacer } from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import { getCustomTranslation, getEncodingProductFields, getField } from 'stylewhere/shared/utils'

interface Props {
  onClose: () => void
  identifier: ConfigurationDetail
  visible: boolean
}

interface State {
  events?: any[]
  identifier?: ItemIdentifier
  loader: boolean
  item?: TmrItem
  productionOrderRow?: ProductionOrderRow
  workOrderRow?: WorkOrderRow
}

export class ItemIdentifierInfoModal extends Component<Props, State> {
  state: State = {
    identifier: undefined,
    events: undefined,
    loader: true
  }

  fields: ProductInfoField[] = [
    { path: 'code', label: __(T.misc.code) },
    { path: 'tid', label: 'Tid' },
    { path: 'type', label: __(T.misc.type) },
    { path: 'role', label: __(T.misc.role) },
    { path: 'status', label: __(T.misc.status) }
  ]

  componentDidMount() {
    void this.init()
  }

  init = async () => {
    const { identifier } = this.props
    try {
      const ret = await ItemIdentifiers.get(identifier.identifierId)

      const workOrderRow: WorkOrderRow | undefined = await this.getWorkOrderRow(identifier.workOrderRowId)
      const productionOrderRow: ProductionOrderRow | undefined = await this.getProductionOrder(
        identifier.productionOrderRowId
      )
      const item: TmrItem | undefined = await this.getItem(ret.itemId)

      const data = await IdentifierEvents.getIdentifierEvents(ret.id)

      this.setState({ loader: false, identifier: ret, item, workOrderRow, productionOrderRow, events: data.content })
    } catch {
      this.setState({ loader: false })
    }
  }

  getProductionOrder = async (productionOrderRowId?: string): Promise<ProductionOrderRow | undefined> => {
    if (!productionOrderRowId) return undefined
    try {
      return await EncodingProductionOrderRows.get(productionOrderRowId)
    } catch {
      return undefined
    }
  }

  getWorkOrderRow = async (workOrderRowId?: string): Promise<WorkOrderRow | undefined> => {
    if (!workOrderRowId) return undefined
    try {
      return await WorkOrderRows.get(workOrderRowId)
    } catch {
      return undefined
    }
  }

  getItem = async (itemId?: string): Promise<TmrItem | undefined> => {
    if (!itemId) return undefined
    try {
      return await Items.get(itemId)
    } catch {
      return undefined
    }
  }

  getOrderData = () => {
    const { productionOrderRow, workOrderRow } = this.state
    return productionOrderRow?.productionOrder ?? workOrderRow?.workOrder
  }

  getProductData = () => {
    const { productionOrderRow, workOrderRow, item } = this.state
    return productionOrderRow?.product ?? workOrderRow?.product ?? item?.product
  }

  render() {
    const { onClose, visible } = this.props
    const { loader, identifier, productionOrderRow, workOrderRow, events } = this.state
    const order = this.getOrderData()
    const product = this.getProductData()
    const orderDetails = productionOrderRow
      ? EncodingExtensions.getProductionOrderDetails({
          productionOrderRow
        })
      : EncodingExtensions.getWorkOrderDetails({ workOrderRow })

    return (
      <Modal size="6xl" onClose={onClose} visible={visible} fullContent showCloseButton>
        <Box flex row>
          <Box flex={3} ml={35} mt={10} style={{ minWidth: 0 }}>
            <ModalTitle
              title={__(T.misc.item_identifier_info)}
              data-testid={`identifiers-modal-title`}
              style={{ flex: 0, paddingTop: 20, paddingBottom: 15 }}
            />
            <Box
              flex
              ph={35}
              pb={0}
              pr={0}
              pl={0}
              mr={35}
              mb={20}
              style={{
                height: 'calc(80vh - 40px)',
                maxHeight: 'calc(80vh - 40px)',
                overflow: 'auto',
                overflowX: 'hidden'
              }}
            >
              {!loader && identifier && (
                <>
                  <Box p={20} pb={5} m={2} mt={5} boxShadow={'0px 1px 4px rgba(0, 0, 0, 0.15)'} borderRadius={10}>
                    {this.fields.map((field, index) => {
                      return (
                        <ProductInfoBox.Field
                          key={`${index}-${field.label}`}
                          label={getCustomTranslation(field.label)}
                          rowLabel={field.label}
                          value={getField(identifier, field.path) ?? '---'}
                        />
                      )
                    })}
                  </Box>
                  <Spacer height="10" />
                  {order && (
                    <Box mb={10}>
                      <InfoBox
                        title={productionOrderRow ? __(T.misc.production_order) : __(T.misc.work_order)}
                        subtitle={orderDetails.subtitle}
                        data={orderDetails.data}
                        fields={productionOrderRow ? EncodingExtensions.getProductionOrderFields() : []}
                      />
                    </Box>
                  )}
                  {product && (
                    <ProductInfoBox
                      paddingCard={'20px 20px 20px 20px'}
                      shadowCard={'0px 1px 4px rgba(0, 0, 0, 0.15)'}
                      marginCard={'2px'}
                      product={product}
                      fields={getEncodingProductFields()}
                    />
                  )}
                </>
              )}
              {loader && (
                <Box mt={20} center>
                  <Spinner color="#000" size="xl" />
                </Box>
              )}
            </Box>
          </Box>
          <ItemEventBox title={__(T.misc.item_identifier_events)} events={events} />
        </Box>
      </Modal>
    )
  }
}
